<template>
  <div class="signature-wrapper">
    <div class="signature-pad" :style="{ width: width, height: height }">
      <canvas ref="canvas" class="canvas" :style="{ width: width, height: height }"></canvas>
    </div>
    <div v-if="showBtn" class="btn" :class="needRotate && 'rotate-btn'">
      <slot name="btn">
        <span class="clear-btn" @click="clear"><i class="icon iconfont iconRefresh"></i></span>
      </slot>
    </div>
  </div>
</template>

<script>
import SignaturePad from 'signature_pad'

export default {
  name: 'SignaturePad',
  props: {
    width: {
      type: String,
      default: '500px'
    },
    height: {
      type: String,
      default: '300px'
    },
    needRotate: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      default: () => {}
    },
    scaleToDevicePixelRatio: {
      type: Boolean,
      default: true
    },
    showBtn: {
      type: Boolean,
      default: true
    }
  },
  data () {
    const vm = this
    return {
      defaultOptions: {
        dotSize: 1,
        minWidth: 3,
        maxWidth: 3.5,
        throttle: 10,
        minDistance: 2,
        backgroundColor: '#fff',
        penColor: 'black',
        velocityFilterWeight: 0.7,
        onBegin: () => {
          const canvas = vm.$refs.canvas
          const rect = canvas.getBoundingClientRect()
          vm.signaturePad.clear() // 清除之前的签名以便新设置生效
          vm.signaturePad.off() // 移除所有事件监听器
          vm.signaturePad.on('beginStroke', (event) => {
            event.preventDefault()
            const x = event.clientX - rect.left
            const y = event.clientY - rect.top
            vm.signaturePad.toData().push({ x, y })
          })
        },
        onEnd: () => {}
      },
      signaturePad: null
    }
  },
  mounted () {
    this.initSignaturePad()
    window.addEventListener('resize', this.resizeCanvas)
  },
  beforeDestroy () {
    if (this.signaturePad) {
      // Remove all event listeners.
      this.signaturePad.off()
    }
  },
  methods: {
    initSignaturePad () {
      const canvas = this.$refs.canvas
      this.signaturePad = new SignaturePad(canvas, {
        ...this.defaultOptions,
        ...this.options
      })
      this.resizeCanvas()
    },
    resizeCanvas () {
      const canvas = this.$refs.canvas
      const data = this.signaturePad.toData()
      const ratio = this.scaleToDevicePixelRatio
        ? Math.max(window.devicePixelRatio || 1, 1)
        : 1

      canvas.width = canvas.offsetWidth * ratio
      canvas.height = canvas.offsetHeight * ratio
      canvas.getContext('2d').scale(ratio, ratio)

      this.signaturePad.clear()
      this.signaturePad.fromData(data)
    },
    clear () {
      if (this.signaturePad) {
        this.signaturePad.clear()
      }
    },
    isEmpty () {
      if (this.signaturePad) {
        return this.signaturePad.isEmpty()
      }
      return true
    },
    async toDataURL () {
      if (this.signaturePad) {
        if (this.needRotate) {
          return await this.rotateBase64Img(this.signaturePad.toDataURL(), -90)
        }
        return this.signaturePad.toDataURL()
      }
    },
    fromDataURL (dataURL) {
      if (this.signaturePad && dataURL) {
        this.signaturePad.fromDataURL(dataURL)
      }
    },
    async rotateBase64Img (src, deg) {
      return new Promise((resolve, reject) => {
        if (typeof src !== 'string' || !src.startsWith('data:image/')) {
          return reject(new Error('Invalid Base64 image source'))
        }

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        let imgW
        let imgH
        let size

        if (deg % 90 !== 0) {
          return
        }
        deg < 0 && (deg = (deg % 360) + 360)
        const quadrant = (deg / 90) % 4
        // 裁剪坐标
        const cutCoordinate = { sx: 0, sy: 0, ex: 0, ey: 0 }

        const image = new Image()

        image.crossOrigin = 'anonymous'
        image.src = src

        image.onload = function () {
          imgW = image.width
          imgH = image.height
          size = imgW > imgH ? imgW : imgH

          canvas.width = size * 2
          canvas.height = size * 2
          switch (quadrant) {
            case 0:
              cutCoordinate.sx = size
              cutCoordinate.sy = size
              cutCoordinate.ex = size + imgW
              cutCoordinate.ey = size + imgH
              break
            case 1:
              cutCoordinate.sx = size - imgH
              cutCoordinate.sy = size
              cutCoordinate.ex = size
              cutCoordinate.ey = size + imgW
              break
            case 2:
              cutCoordinate.sx = size - imgW
              cutCoordinate.sy = size - imgH
              cutCoordinate.ex = size
              cutCoordinate.ey = size
              break
            case 3:
              cutCoordinate.sx = size
              cutCoordinate.sy = size - imgW
              cutCoordinate.ex = size + imgH
              cutCoordinate.ey = size + imgW
              break
          }

          ctx.translate(size, size)
          ctx.rotate((deg * Math.PI) / 180)
          ctx.drawImage(image, 0, 0)

          const imgData = ctx.getImageData(
            cutCoordinate.sx,
            cutCoordinate.sy,
            cutCoordinate.ex,
            cutCoordinate.ey
          )

          if (quadrant % 2 === 0) {
            canvas.width = imgW
            canvas.height = imgH
          } else {
            canvas.width = imgH
            canvas.height = imgW
          }
          ctx.putImageData(imgData, 0, 0)
          resolve(canvas.toDataURL())
        }

        image.onerror = () => {
          reject(new Error('Failed to load image'))
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.signature-wrapper {
  position: relative;
  .signature-pad {
    border: 1px solid #ccc;
  }

  .btn {
    position: absolute;
    top: 8px;
    right: 10px;
    .clear-btn {
      background-color: #fff;
      .icon {
        color: #0096cc;
        font-size: 22px;
        cursor: pointer;
      }
    }
  }
  .rotate-btn {
    top: auto;
    bottom: 8px;
  }
}
</style>
